import { useMemo } from "react";

import { useProbateAnswers } from "@/shared/hooks/useWillAnswer";
import { useCanonicalAddress } from "@/shared/hooks/useCanonicalAddress";
import { useEstateSizeAnswers } from "@/shared/hooks/useEstateSizeAnswers";
import { getAuthorityDocumentName } from "@/shared/constants/legalAuthorityDocuments";
import { getAuthorityPath, type AuthorityPath } from "@/shared/utils/authorityPath";

export type { AuthorityPath };

export interface AuthorityContext {
  path: AuthorityPath | null;
  hasWill: string | null;
  state: string | null;
  county: string | null;
  countyLabel: string;
  isNY: boolean;
  authorityDocumentName: string;
  isReady: boolean;
}

export function useAuthorityContext(estateId: number | undefined): AuthorityContext {
  const {
    hasWill,
    probateState,
    probateCounty,
    isLoading: isProbateLoading,
  } = useProbateAnswers(estateId);

  const { data: canonicalAddress, isLoading: isAddressLoading } = useCanonicalAddress(
    estateId,
    "domicile",
  );

  const { savedAnswers: estateSizeAnswers, isFetched: isSizeFetched } =
    useEstateSizeAnswers(estateId);

  return useMemo(() => {
    const resolvedState = canonicalAddress?.state ?? probateState ?? null;
    const rawCounty = canonicalAddress?.county ?? probateCounty ?? null;
    const county = rawCounty
      ? rawCounty.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
      : null;

    return {
      path: getAuthorityPath(hasWill, estateSizeAnswers?.["estate-amount"] === "under"),
      hasWill: hasWill ?? null,
      state: resolvedState,
      county,
      countyLabel: county ? `${county} County` : "your county",
      isNY: resolvedState === "NY",
      authorityDocumentName: getAuthorityDocumentName(resolvedState, hasWill),
      isReady: !isProbateLoading && !isAddressLoading && isSizeFetched,
    };
  }, [
    hasWill,
    probateState,
    probateCounty,
    canonicalAddress,
    estateSizeAnswers,
    isProbateLoading,
    isAddressLoading,
    isSizeFetched,
  ]);
}
